import { useCallback, useState } from 'react';
import List from '@mui/material/List';
import { useTranslation } from 'react-i18next';
import { FilterSection } from 'common/components/Filters/FilterSection';
import { FiltersDialog, FiltersDialogProps } from 'common/components/Filters/FiltersDialog';
import { FilterListItem } from 'common/components/Filters/FilterListItem';
import Box from '@mui/material/Box';

import { FilterBox } from 'common/components/Filters/FilterBox';
import { Actions, Subjects } from 'permissions/ability';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { AccountSearchInput } from './Components/Inputs/AccountSearchInput';
import { ProductCodesSearchInput } from './Components/Inputs/ProductCodesSearchInput';
import { TagsSearchInput } from './Components/Inputs/TagsSearchInput';
import { ConnectionIdSearchInput } from './Components/Inputs/ConnectionIdSearchInput';
import { OrderNumberSearchInput } from './Components/Inputs/OrderNumberSearchInput';
import { MnoSearchInput } from './Components/Inputs/MNOSearchInput';
import { SessionStatusInput } from './Components/Inputs/SessionStatusInput';
import { SimProfilesIdSearchInput } from './Components/Inputs/SimProfilesIdSearchInput';
import { RadioSection } from 'common/components/Filters/IdFilterSelector/RadioSection';
import { IccidRangeSearch } from './Components/Inputs/IccidRangeSearchInput';
import { simFiltersAtom } from 'atoms/filters/sim';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { SimProfilesCustomFieldInputs } from './Components/Inputs/CutomFieldInputs';
import { useAbility } from 'permissions/hooks/useAbility';
import { MNOStatusInput } from './Components/Inputs/MNOStatusInput';
import { useEnabledCustomFieldFromQueryCache } from 'admin/customFields/hooks/useEnabledCustomFieldFromQueryCache';
import { SimProfilesSuscriptionStatusInput } from './Components/Inputs/SimProfilesSuscriptionStatusInput';

type SimFiltersDialogProps = Pick<FiltersDialogProps, 'onCancel' | 'onClear' | 'onClose'> & {
  onApply: (filters: any) => void;
};

export const SimFiltersDialog = ({ onApply, onClear, ...props }: SimFiltersDialogProps) => {
  const { t } = useTranslation();

  const filters = useAtomValue(simFiltersAtom);
  const resetFilters = useResetAtom(simFiltersAtom);

  const [selectedSearch, setSelectedSearch] = useState<'search' | 'iccidRange'>(() => {
    if (filters.iccidFrom !== '' || filters.iccidTo !== '') {
      return 'iccidRange';
    }

    return 'search';
  });

  const onClearClicked = useCallback(() => {
    resetFilters();
    onClear();
  }, []);

  const onApplyClicked = useCallback(() => {
    if (Number.parseInt(filters.iccidFrom) > Number.parseInt(filters.iccidTo)) {
      onApply({ ...filters, iccidFrom: '', iccidTo: '' });
    } else {
      onApply(filters);
    }
  }, [filters]);

  const ability = useAbility();

  const customFieldsEnabled = ability.can(Actions.read, Subjects.customFields);

  const { data: customFieldsData } = useEnabledCustomFieldFromQueryCache();

  const customFieldsAvailable =
    customFieldsEnabled && customFieldsData && customFieldsData.length > 0;

  return (
    <FiltersDialog
      {...props}
      open={true}
      onApply={onApplyClicked}
      onClear={onClearClicked}
      oneColumn={!customFieldsAvailable}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
        <FilterSection title={t('common.idSearch')} role="region" aria-label={t('common.idSearch')}>
          <FilterBox>
            <RadioSection
              options={['search', 'iccidRange']}
              value={selectedSearch}
              onValueChange={(newValue) => setSelectedSearch(newValue as 'search' | 'iccidRange')}
              labels={{
                search: 'Search',
                iccidRange: 'ICCID Range',
              }}
            >
              {(value) => {
                return (
                  <Box>
                    <Box
                      data-selector="search"
                      sx={{ opacity: value === 'search' ? 1 : 0 }}
                      aria-hidden={value !== 'search'}
                    >
                      <SimProfilesIdSearchInput />
                    </Box>
                    <Box
                      data-selector="iccidRange"
                      sx={{ opacity: value === 'iccidRange' ? 1 : 0 }}
                      aria-hidden={value !== 'iccidRange'}
                    >
                      <IccidRangeSearch />
                    </Box>
                  </Box>
                );
              }}
            </RadioSection>
          </FilterBox>
        </FilterSection>
        <FilterSection title={t('simInventory.sim')}>
          <List>
            <FilterListItem label={t('simInventory.subscriptionStatus')} htmlFor="select status">
              <SimProfilesSuscriptionStatusInput />
            </FilterListItem>
            <FilterListItem label={t('simInventory.sessionStatus')} htmlFor="select status">
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <SessionStatusInput />
              </Box>
            </FilterListItem>
            <FilterListItem label={t('simInventory.mnoStatus')} htmlFor="mnoStatus">
              <MNOStatusInput />
            </FilterListItem>
            <SimInventoryCan I={Actions.filter} a={Subjects.internalData}>
              <FilterListItem label="Account" htmlFor="account">
                <AccountSearchInput />
              </FilterListItem>
            </SimInventoryCan>
            <FilterListItem label="Product Code" htmlFor="productCodes">
              <ProductCodesSearchInput />
            </FilterListItem>
            <FilterListItem
              label={t('simInventory.mobileNetworkOperator')}
              htmlFor="mobileNetworkOperator"
            >
              <MnoSearchInput />
            </FilterListItem>
            <FilterListItem label={'Tag'} htmlFor="tag">
              <TagsSearchInput />
            </FilterListItem>
          </List>
        </FilterSection>
        <FilterSection title={t('common.order')}>
          <List>
            <FilterListItem label={t('common.connectionId')} htmlFor="connectionId">
              <ConnectionIdSearchInput />
            </FilterListItem>
            <FilterListItem label={t('simInventory.orderNumber')} htmlFor="orderNumber">
              <OrderNumberSearchInput />
            </FilterListItem>
          </List>
        </FilterSection>
      </Box>
      {customFieldsAvailable && (
        <FilterSection title={t('customFields.customFields')}>
          <List>
            <SimProfilesCustomFieldInputs />
          </List>
        </FilterSection>
      )}
    </FiltersDialog>
  );
};
